import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { getStatusHistory } from "../../../services/stationStatus";
import { getStationDetails } from "../../../services/stations";
import { StatusPeriod } from "../../../types/alerting";
import { formatDuration } from "../../../utils/formatDuration";
import { statusStyle } from "../../../utils/statusPresentation";

interface Charger {
  uid: string;
  label: string;
  period: StatusPeriod | null;
}

/** The current status of every EVSE at a station, which no single endpoint can answer. */
const LocationChargers = ({ locationId }: { locationId: string }) => {
  const navigate = useNavigate();
  const [chargers, setChargers] = useState<Charger[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setState("loading");
      try {
        const details = await getStationDetails(locationId);
        const evses = details?.evses ?? [];

        const resolved = await Promise.all(
          evses.map(async (evse: any) => {
            const page = await getStatusHistory("EVSE", evse.uid, { limit: 1 }).catch(() => null);
            return {
              uid: evse.uid,
              label: evse.evse_id ?? evse.uid,
              period: page?.content?.[0] ?? null,
            };
          }),
        );

        if (!cancelled) {
          setChargers(resolved);
          setState("ready");
        }
      } catch {
        if (!cancelled) setState("failed");
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  if (state === "loading") {
    return <span className="text-color-secondary" data-testid="chargers-loading">Loading EVSEs…</span>;
  }

  if (state === "failed") {
    return (
      <div data-testid="chargers-failed">
        <Message severity="error" text="Could not load this station's EVSEs" />
      </div>
    );
  }

  if (chargers.length === 0) {
    return <span className="text-color-secondary" data-testid="chargers-empty">No EVSEs at this station.</span>;
  }

  return (
    <div className="flex flex-column gap-2" data-testid="location-chargers">
      {chargers.map((charger) => (
        <div
          key={charger.uid}
          className="flex align-items-center justify-content-between gap-3 p-2 border-round surface-100 cursor-pointer"
          onClick={() => navigate(`/alerting/status/EVSE/${encodeURIComponent(charger.uid)}`)}
        >
          <span className="font-medium">{charger.label}</span>
          {charger.period ? (
            <span className="flex align-items-center gap-2">
              <Tag value={charger.period.status} style={statusStyle(charger.period.status)} />
              <span className="text-xs text-color-secondary">
                for {formatDuration(charger.period.durationSeconds)}
              </span>
            </span>
          ) : (
            <span className="text-xs text-color-secondary">No status recorded</span>
          )}
        </div>
      ))}
    </div>
  );
};

export default LocationChargers;
